/**
 * Phase 4.10 (H4) — Live Inserat-Qualitäts-Score.
 *
 * Sitzt unter der SectionSidebar in CreateListingPage und zeigt einen
 * gewichteten Score 0-100 + sortierte Tipps zur Verbesserung. Jeder Tipp
 * verlinkt per scroll-to-testid auf das fehlende Feld.
 *
 * Lädt deboucned 800 ms nach jeder Form-Änderung (kein LLM-Call —
 * deterministisch in <5 ms im Backend).
 */
import { useEffect, useRef, useState } from "react";
import { CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import api from "../../lib/api";

const LEVEL_STYLES = {
    high:   { color: "#059669", bar: "bg-emerald-500",   bg: "bg-emerald-50",  border: "border-emerald-300", label: "Hervorragend" },
    medium: { color: "#D97706", bar: "bg-[#FFCC00]",     bg: "bg-amber-50",    border: "border-amber-300",   label: "Solide" },
    low:    { color: "#DC2626", bar: "bg-red-500",       bg: "bg-red-50",      border: "border-red-300",     label: "Ausbaufähig" },
};

export default function QualityScoreCard({ form, compact = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data: resp } = await api.post("/listings/ai-quality-score", form);
                setData(resp);
            } catch {
                /* silent — score is optional UI */
            } finally {
                setLoading(false);
            }
        }, 800);
        return () => timer.current && clearTimeout(timer.current);
    }, [form]);

    const jumpTo = (testid) => {
        if (!testid) return;
        // Feb-2026 Iter 28 (User-Wunsch 25 May): Robustes Sprung-Verhalten —
        //   1. Element direkt finden, falls sichtbar → scrollen + highlighten.
        //   2. Falls Element in einer eingeklappten Section liegt: ALLE
        //      Collapsible-Vorfahren mit aria-expanded="false" aufklappen.
        //   3. Falls Element gar nicht existiert (z.B. Beschreibung war einst
        //      removed): Sprung zur korrespondierenden Section anhand des
        //      testid-Präfixes (z.B. "form-description" → "description-section").
        const SECTION_FALLBACK = {
            "form-description": "service-section",
            "form-vin": "disposition-section-toggle",
            "form-video-url": "video-upload-section",
            "image-upload-section": "images-grid",
        };

        const highlight = (el) => {
            try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }
            el.classList.add("ring-2", "ring-[#FFCC00]", "ring-offset-2");
            setTimeout(() => el.classList.remove("ring-2", "ring-[#FFCC00]", "ring-offset-2"), 1800);
            if (typeof el.focus === "function") {
                try { el.focus({ preventScroll: true }); } catch { /* ignore */ }
            }
        };

        const expandAncestors = (target) => {
            // Klettere den Baum hoch und klicke jeden Collapsible-Toggle auf,
            // der eingeklappt ist (aria-expanded="false"). Funktioniert für
            // CollapsibleCard + SectionToggle (beide nutzen aria-expanded).
            let node = target.parentElement;
            const seen = new Set();
            while (node && !seen.has(node)) {
                seen.add(node);
                const btn = node.querySelector?.(':scope > div > [aria-expanded="false"], :scope > [aria-expanded="false"]');
                if (btn) {
                    try { btn.click(); } catch { /* ignore */ }
                }
                node = node.parentElement;
            }
        };

        const tryFind = (attempt = 0) => {
            const el = document.querySelector(`[data-testid="${testid}"]`);
            if (el) {
                expandAncestors(el);
                setTimeout(() => highlight(el), 180);
                return true;
            }
            if (attempt === 0) {
                // Fallback: zur Section-Card scrollen + aufklappen
                const sectionTestid = SECTION_FALLBACK[testid] || testid.replace(/^form-/, "").replace(/-/g, "-") + "-section";
                const section = document.querySelector(`[data-testid="${sectionTestid}"]`);
                if (section) {
                    const toggle = section.querySelector('[aria-expanded="false"]');
                    if (toggle) {
                        try { toggle.click(); } catch { /* ignore */ }
                    }
                    setTimeout(() => tryFind(1), 250);
                    setTimeout(() => {
                        // Wenn nach Aufklappen das Feld erscheint, wurde schon
                        // gehighlighted; sonst scrollen wir wenigstens zur Section.
                        const found = document.querySelector(`[data-testid="${testid}"]`);
                        if (!found) highlight(section);
                    }, 500);
                    return true;
                }
            }
            return false;
        };
        tryFind();
    };

    if (!data) {
        return (
            <div className={`bg-white border border-gray-200 rounded-sm shadow-sm ${compact ? "px-3 py-2 inline-flex items-center gap-2" : "p-3 mt-3"}`} data-testid="quality-score-card-loading">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Inserats-Qualität</div>
                <div className={`text-xs text-gray-400 ${compact ? "" : "mt-2"}`}>Wird ausgewertet…</div>
            </div>
        );
    }

    const style = LEVEL_STYLES[data.level] || LEVEL_STYLES.low;

    // Feb-2026: Compact-Modus (horizontal, neben dem h1) — ohne Tipps-Liste,
    // ohne progress bar; nur Score + Level + Ranking-Pille.
    if (compact) {
        const topTip = (data.missing && data.missing[0]) || null;
        return (
            <div
                className={`border-2 rounded-sm shadow-sm px-3 py-2 flex items-center gap-3 ${style.bg} ${style.border}`}
                data-testid="quality-score-card"
            >
                <div className="flex flex-col items-start leading-tight">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-700 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Qualität
                    </div>
                    <span
                        className="text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: style.color }}
                        data-testid="quality-score-level"
                    >
                        {style.label}
                    </span>
                </div>
                <div className="flex items-baseline gap-1 border-l border-gray-300 pl-3">
                    <span
                        className="font-display font-black text-2xl font-mono-num leading-none"
                        style={{ color: style.color }}
                        data-testid="quality-score-value"
                    >
                        {data.score}
                    </span>
                    <span className="text-[10px] text-gray-500 font-mono-num">/100</span>
                </div>
                {topTip && (
                    <button
                        type="button"
                        onClick={() => jumpTo(topTip.testid)}
                        className="hidden md:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-700 bg-white border border-gray-300 hover:border-[#111827] rounded-sm px-2 py-0.5 transition-colors"
                        title={topTip.hint}
                        data-testid={`quality-tip-${topTip.testid}`}
                    >
                        <AlertCircle className="w-3 h-3 text-red-500" />
                        Tipp: {topTip.label} <span className="text-gray-400 font-mono-num">+{topTip.points}</span>
                    </button>
                )}
                {(!topTip || data.all_missing_count === 0) && (
                    <span className="hidden md:inline-flex items-center gap-1 text-[10px] text-emerald-700 font-bold">
                        <CheckCircle2 className="w-3 h-3" /> alles erfüllt
                    </span>
                )}
            </div>
        );
    }

    return (
        <div
            className={`border-2 rounded-sm p-3 shadow-sm mt-3 ${style.bg} ${style.border}`}
            data-testid="quality-score-card"
        >
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-700 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Qualität
                </div>
                <span
                    className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm text-white"
                    style={{ backgroundColor: style.color }}
                    data-testid="quality-score-level"
                >
                    {style.label}
                </span>
            </div>
            <div className="flex items-baseline justify-between mt-2">
                <span
                    className="font-display font-black text-3xl font-mono-num"
                    style={{ color: style.color }}
                    data-testid="quality-score-value"
                >
                    {data.score}
                </span>
                <span className="text-[10px] text-gray-500 font-mono-num">/ 100</span>
            </div>
            <div className="h-1.5 bg-white border border-gray-200 rounded-sm overflow-hidden mt-2">
                <div
                    className={`h-full ${style.bar} transition-all duration-500`}
                    style={{ width: `${data.score}%` }}
                />
            </div>
            {data.missing && data.missing.length > 0 ? (
                <div className="mt-3" data-testid="quality-score-tips">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1.5">
                        Tipps ({data.all_missing_count})
                    </div>
                    <ul className="space-y-1">
                        {data.missing.slice(0, 5).map((m, i) => (
                            <li key={`${m.label}-${i}`}>
                                <button
                                    type="button"
                                    onClick={() => jumpTo(m.testid)}
                                    className="w-full flex items-start gap-1.5 text-left text-[11px] hover:bg-white rounded-sm px-1.5 py-1 transition-colors group"
                                    data-testid={`quality-tip-${m.testid}`}
                                    title={m.hint}
                                >
                                    <AlertCircle
                                        className={`w-3 h-3 mt-0.5 shrink-0 ${m.bonus ? "text-gray-400" : "text-red-500"}`}
                                        strokeWidth={2.4}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-800 leading-tight">
                                            {m.label}
                                            {m.bonus && <span className="ml-1 text-[9px] text-gray-400">(Bonus)</span>}
                                        </div>
                                        <div className="text-[10px] text-gray-500 line-clamp-2 group-hover:text-gray-700">
                                            {m.hint}
                                        </div>
                                    </div>
                                    <span className="text-[9px] font-mono-num text-gray-400 shrink-0">+{m.points}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <div className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Perfekt — alle Kriterien erfüllt.
                </div>
            )}
            {loading && (
                <div className="mt-2 text-[9px] text-gray-400 italic">aktualisiere…</div>
            )}
        </div>
    );
}
